import { EditorTools } from "@progress/kendo-react-editor";
import CryptoJS from "crypto-js";
import { format, formatDistance, isToday, isYesterday } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import _, { isEmpty } from "lodash";
import React from "react";
import * as sanitizeHtml from "sanitize-html";
import { number } from "../config/";
import { dateFormat, icon, paletteSettings } from "../config/constants";
import { route } from "../config/index";
import InsertImage from "../shared/components/Kendo/InsertImageTool";
import { isDateValid } from "../shared/validators/validator";
import store from "../store";
import { splitString } from "../utils/index";

/**
 * converts time to current date for api call
 * @param {*} time
 * @returns {Date} date
 */
export const getDateFromTime = (time) => {
  let date = new Date();
  if (!isEmpty(time)) {
    let splitTime = splitString(time, ":"),
      hours = splitTime[number.ZERO],
      minutes = splitTime[number.ONE];
    date.setHours(hours, minutes, number.ZERO);
  }
  return date;
};
/**
 * change format of the datetime to DD MMM YY
 * @returns formateted date Eg. 28 Feb 22
 */
export const formatDate = (dateString) => {
  return isDateValid(dateString) ? convertToTimeZone(dateString, dateFormat.COMMON_DATE_FORMAT) : null;
};

/**
 * change format of the datetime to DD MMM
 * @returns formateted date Eg. 28 Feb
 */
export const formatDateWithoutYear = (dateString) => {
  return isDateValid(dateString) ? convertToTimeZone(dateString, dateFormat.DATE_FORMAT_WITHOUT_YEAR) : null;
};


/**
 * converts time to current date for api call for task grid
 * @returns formateted date Eg. 28 Feb 22
 * 
 */
  export const formatDateWithoutTimeZone = (dateString) => {
    return isDateValid(dateString) ? format(new Date(dateString), dateFormat.COMMON_DATE_FORMAT) : null;
  };

 /**
 * converts time to current date for api call for task grid without time zone
 * @returns formateted date Eg. 28 Feb
 */
export const formatDateWithoutYearWithoutTimeZone = (dateString) => {
  return isDateValid(dateString) ? format(new Date(dateString), dateFormat.DATE_FORMAT_WITHOUT_YEAR) : null;
};

/**
 * Format date for PDF export
 * @param {string} value - Date value
 * @returns {string} Formatted date
 * @author Bhavana
 */
export const formatPdfExportDate = (value) => {
  if (!value) return "";
  if (!isDateValid(value)) return value;
  return format(new Date(value), "dd-MM-yyyy");
};


/**
 * change the time in string to date
 * @param {*} time
 * @returns
 */
export const parseTimeStringToDate = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};
/**
 *  provides respective icon for the given priority
 * @param {*} priority
 * @returns icon
 */
export const getPriorityIcon = (priority) => {
  switch (priority) {
    case "Low":
      return icon.PRIORITY_LOW;
    case "Medium":
      return icon.PRIORITY_MEDIUM;
    case "High":
      return icon.PRIORITY_HIGH;
    case "Highest":
      return icon.PRIORITY_HIGHEST;
  }
};

/**
 * Get key value pairs from data array
 * @param {*} data
 * @returns {Object} keyVal
 */

export const getDataValues = (data) => {
  let keyVal = {};
  for (const arr in data) {
    data[arr].forEach((item, index) => {
      keyVal[item.key] = item.value;
    });
  }
  return keyVal;
};

/**
 *
 * @param {Date} dateTime
 * @returns time string in format hh:mm
 */
export const getTimeFromDate = (dateTime) => {
  let date = new Date(dateTime),
    time = date.toTimeString(),
    splitTime = splitString(time, ":");
  if (splitTime) {
    return splitTime[number.ZERO] + ":" + splitTime[number.ONE];
  }
};

/**
 * converts time to UTC timezone for api call using date-fns
 * @param {*} dateTime
 * @returns {string} time in HH:MM format (UTC)
 */
export const getTimeFromDateUTC = (dateTime) => {
  return formatInTimeZone(new Date(dateTime), 'UTC', 'HH:mm');
};

/**
 * To fetch query parameters from url
 * @returns router query location search object
 */
export const getQueryParams = (params, search) => {
  let queryString = new URLSearchParams(search);
  return queryString.get(params);
};

/**
 * Generate Random hex code for colors
 * @returns hex code of color
 * @author Prachi Jain
 */
export const hexCodeGenerator = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = number.ZERO; i < number.SIX; i++) {
    color += letters[Math.floor(Math.random() * number.SIXTEEN)];
  }
  return color;
};

/**
 * comment date and time format
 * @returns formateted date
 *
 */
export const commentDateAndTimeFormat = (time) => {
  return isDateValid(time) ? convertToTimeZone(time, dateFormat.DATE_FORMAT) : null;
};

/**
 * last activity date and time format
 * @returns formateted date
 *
 */
export const lastActivityDateAndTime = (time) => {
  return convertToTimeZone(time, dateFormat.LAST_ACTIVITY);
};

/**
 * last reply notifier
 * @returns last reply notification
 *
 */
export const lastReplyNotify = (lastReplyDateAndTime) => {
  const state = store.getState();
  const userTimezone = state?.auth?.user?.timezone;
  const zonedDate = utcToZonedTime(lastReplyDateAndTime, userTimezone);
  const now = utcToZonedTime(new Date().toISOString(), userTimezone);
  const timeDiff = formatDistance(zonedDate, now, { addSuffix: true });

  if (timeDiff.includes("minutes") || timeDiff.includes("minute") || timeDiff.includes("hour") || timeDiff.includes("hours")) {
    return "today";
  } else if (timeDiff.includes("1 day")) {
    return "yesterday";
  } else if (timeDiff.includes("month")) {
    return "a month ago";
  } else if (timeDiff.includes("year") || timeDiff.includes("years")) {
    return "long time ago";
  } else {
    return timeDiff + " ago";
  }
};

/**
 * Creates URL for task sidebar toggle
 * @param {Integer} projectId
 * @param {Object} task
 * @param {String} pageUrl
 */
export const getTaskUrl = (taskId) => {
  let url = route.PRIVATE_ROUTE.TASK_SIDEBAR.PATH;
  if (taskId) {
    url += `?tId=${taskId}`;
  }
  return url;
};

/**
 * Opens task URL in a new browser tab.
 * @param {string|number} taskId - Task id
 */
export const openTaskInNewTab = (taskId) => {
  if (taskId) {
    const taskUrl = `${window.location.origin}${getTaskUrl(taskId)}`;
    window.open(taskUrl, "_blank", "noopener,noreferrer");
  }
};

/**
 * Creates Project Page URL for sidebar toggle
 * @param {Object} project
 */
export const replaceProjectUrl = (project) => {
  if (project && project.ProjectId && project.ProjectCode) {
    let path = route.PRIVATE_ROUTE.PROJECT.PATH + "/" + project.ProjectId + "/" + project.ProjectCode;
    window.history.replaceState(null, null, path);
    return path;
  }
};

/**
 * Creates Project Page URL for generic projects and return task path for personal projects
 * @param {Object} project
 * @return {string} url
 */
export const getProjectUrl = (project) => {
  const state = store.getState();
  const { auth } = state;
  return auth?.user?.myProjectId != project.projectId
    ? `${route.PRIVATE_ROUTE.TASKS.PATH}/${project.projectId}/${encodeURIComponent(project.projectName)}`
    : route.PRIVATE_ROUTE.TASKS.PATH;
};

/**
 * Creates Project Settings URL for generic projects and return task path for personal projects
 * @author Muskan Thakur
 */
export const getProjectSettingsUrl = (project) => {
  const state = store.getState();
  const { auth } = state;
  return auth?.user?.myProjectId != project.projectId
    ? `${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project.projectId}?isAdmin=true`
    : route.PRIVATE_ROUTE.TASKS.PATH;
};

export const getWorkflowUrl = (projectId, projectName, workflowId) => {
  return `${route.PRIVATE_ROUTE.WORKFLOW.PATH}/${projectId}/${encodeURIComponent(projectName)}` + (workflowId ? `/${workflowId}` : "");
};

/**
 * form builder URL 
 * @author Muskan Thakur
 */
export const getFormBuilderEditUrl = (projectId, projectName, workflowId, formId) => {
  if (!projectId || !workflowId || !formId) {
    return "";
  }
  const name = projectName ?? "";
  return `${route.PRIVATE_ROUTE.FORM_BUILDER.PATH}/${projectId}/${encodeURIComponent(name)}/${workflowId}/${formId}`;
};

/**
 * Creates workflow URL with focus panel and optional query params.
 * @author Muskan Thakur
 */
export const getWorkflowPanelUrl = ({ projectId, projectName, workflowId, panelKey, queryParams = {} }) => {
  if (!panelKey) return getWorkflowUrl(projectId, projectName, workflowId);
  const baseUrl = getWorkflowUrl(projectId, projectName, workflowId);
  const params = new URLSearchParams({ focusPanel: panelKey });
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  return `${baseUrl}?${params.toString()}`;
};

export const getCmsFileUrl = (docId) => {
  return `${route.PRIVATE_ROUTE.Y_DOC.PATH}/${docId}`;
};

export const getCmsFolderUrl = (projectId, projectName, folderId) => {
  return `${route.PRIVATE_ROUTE.CMS.PATH}/${projectId}/${encodeURIComponent(projectName)}/${folderId}`;
};

export const getQueueUrl = (queue) => {
  return `${route.PRIVATE_ROUTE.QUEUE_TASKS?.PATH}/${queue?.QueueId}/${encodeURIComponent(queue?.QueueName)}`;
};

export const getProjectFilterUrl = (project, sharedFilterId) => {
  return project.projectId
    ? `${route.PRIVATE_ROUTE.TASKS.PATH}/${project.projectId}/${encodeURIComponent(project.projectName)}/${sharedFilterId}`
    : route.PRIVATE_ROUTE.TASKS.PATH;
};
export const getQueueFilterUrl = (queueId, sharedFilterId) => {
  return `${route.PRIVATE_ROUTE.QUEUE_TASKS?.PATH}/${queueId?.queueId}/${encodeURIComponent(queueId?.queueName)}/${sharedFilterId}`;
};

export const getMaxSortId = (arr, prop) => {
  let max = number.ZERO;
  if (arr) {
    for (let i = number.ZERO; i < arr.length; i++) {
      if (max == null || parseInt(arr[i][prop]) > parseInt(max)) max = arr[i].SortId;
    }
  }
  return max;
};

/**
 * get all the columns of a grid
 * @param {Array} headers
 * @returns allColumns
 */
export const getAllColumnsOfGrid = (headers) => {
  let allColumns = [];
  headers.map(({ code }) => {
    allColumns.push(code);
    return null;
  });
  return allColumns;
};

/**
 * click action of project search result
 * @param {project}
 * @returns {void}
 */
export const projectClickAction = (project, history) => {
  const url = getProjectUrl({ projectId: project.Id, projectName: project.Name });
  history.push(url);
};

/**
 * click action of team search result
 * @param {team}
 * @returns {void}
 */
export const teamClickAction = (team) => {};

/**
 * get highlight text from include given text/description.
 * @param {Description, taskCode}
 * @returns {JSX}
 */
export const getHighlightedText = (description, taskCode) => {
  if (!description || !taskCode) {
    return;
  }
  const getProjectTaskCode = splitString(description, new RegExp(`(${taskCode})`, "gi"));
  return (
    <span>
      {" "}
      {getProjectTaskCode?.map((projectTaskCode, i) => (
        <span key={i} className={projectTaskCode.toLowerCase() === taskCode.toLowerCase() ? "font-weight-bold" : ""}>
          {projectTaskCode}
        </span>
      ))}{" "}
    </span>
  );
};

/**
 * sort Streams Based On Date
 * @param {array} streams
 * @author Shivam
 */
export const sortStreamsBasedOnDate = (streams) => {
  return streams?.sort(
    (b, a) => isDateValid(a?.DateAndTime) && isDateValid(b?.DateAndTime) && new Date(a?.DateAndTime)?.getTime() - new Date(b?.DateAndTime)?.getTime()
  );
};

/**
 * filter unviewedStreams ,  viewedStreams
 * @param {array} streams
 * @returns {array} streams
 * @author Shivam
 */
export const filterViewedUnviewedStreams = (streams) => {
  let newStream = streams.filter((i) => i.Id == number.MINUS_ONE);
  let unviewedStreams = streams.filter((i) => i.IsRead == number.ZERO);
  let viewedStreams = streams.filter((i) => i.IsRead == number.ONE);
  unviewedStreams = sortStreamsBasedOnDate(unviewedStreams);
  viewedStreams = sortStreamsBasedOnDate(viewedStreams);
  return [...newStream, ...unviewedStreams, ...viewedStreams];
};

/**
 * Common function to format date in user timezone with "Today"/"Yesterday" labels.
 * Compares the input date with today and yesterday in the user's timezone (not system timezone).
 * If the date is today → "Today", if yesterday → "Yesterday", else returns formatted date.
 * 
 * @param {string} dateString - ISO or date string from API
 * @param {string} dateFormat - Format string for dates that are not today/yesterday (default: "EEEE MMMM do")
 * @returns {string} date label - "Today", "Yesterday", or formatted date string
 * @author Sejal
 */
export const getFormattedDateWithTodayYesterday = (dateString, dateFormat = "EEEE MMMM do") => {
  if (!dateString) return "";
  const state = store.getState();
  const userTimezone = state?.auth?.user?.timezone || "UTC";
  const zonedDate = utcToZonedTime(new Date(dateString), userTimezone);
  const todayInUserTz = utcToZonedTime(new Date(), userTimezone);
  const yesterdayInUserTz = new Date(todayInUserTz);
  yesterdayInUserTz.setDate(yesterdayInUserTz.getDate() - 1);
  
  const zonedDateStr = formatInTimeZone(zonedDate, userTimezone, "yyyy-MM-dd");
  const todayStr = formatInTimeZone(todayInUserTz, userTimezone, "yyyy-MM-dd");
  const yesterdayStr = formatInTimeZone(yesterdayInUserTz, userTimezone, "yyyy-MM-dd");
  
  if (zonedDateStr === todayStr) {
    return "Today";
  }
  if (zonedDateStr === yesterdayStr) {
    return "Yesterday";
  }
  return convertToTimeZone(dateString, dateFormat);
};

/**
 * return the value of date for activity log (in user timezone).
 * If the activity date is today → "Today", if yesterday → "Yesterday", else full date e.g. "Friday December 31st".
 * @param {string} dateString - ISO or date string from API
 * @returns {string} date label
 * @author Shivam
 */
export const getFormattedDate = (dateString) => {
  return getFormattedDateWithTodayYesterday(dateString, "EEEE MMMM do");
};

/**
 * seperate the activity logs based on date
 * @param {array} entityLogs
 * @returns {object} separatedObjects
 * @author Shivam
 */
export const seperateActivityLogsOnDate = (entityLogs) => {
  const separatedObjects = {};
  for (const object of entityLogs) {
    const createdOn = getFormattedDate(object.CreatedOn);
    if (!separatedObjects[createdOn]) {
      separatedObjects[createdOn] = [];
    }
    separatedObjects[createdOn].push(object);
  }
  return separatedObjects;
};

export const lowercaseKeys = (obj) =>
  Object.keys(obj).reduce((acc, key) => {
    acc[_.camelCase(key)] = obj[key];
    return acc;
  }, {});

/**
 * Converts PascalCase string to camelCase (e.g. "IsTaskUpdateEnabled" → "isTaskUpdateEnabled").
 * @param {string} str - PascalCase or mixed-case string
 * @returns {string} camelCase string, or "" if str is falsy
 */
export const pascalToCamel = (str) => (str ? str.charAt(0).toLowerCase() + str.slice(1) : "");

export const descriptionTools = (workflow) => {
  const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    ForeColor,
    BackColor,
    CleanFormatting,
    Link,
    Unlink,
    InsertTable,
    AddRowBefore,
    AddRowAfter,
    AddColumnBefore,
    AddColumnAfter,
    MergeCells,
    SplitCell,
    DeleteRow,
    DeleteColumn,
    Subscript,
    Superscript,
    Print,
    Pdf,
  } = EditorTools;

  const CustomForeColor = (props) => (
    <ForeColor
      {...props}
      colorPickerProps={{
        title: "ForeColorTitle",
        view: "palette",
        icon: "foreground-color",
        paletteSettings: paletteSettings,
        defaultValue: "green",
      }}
    />
  );
  const CustomBackColor = (props) => (
    <BackColor
      {...props}
      colorPickerProps={{ title: "BackColorTitle", view: "palette", icon: "background", paletteSettings: paletteSettings, defaultValue: "blue" }}
    />
  );
  const tools = [
    Bold,
    Italic,
    Underline,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    Link,
    Unlink,
    Strikethrough,
    CleanFormatting,
    InsertImage,
    InsertTable,
    AddRowBefore,
    AddRowAfter,
    AddColumnBefore,
    AddColumnAfter,
    MergeCells,
    SplitCell,
    DeleteRow,
    DeleteColumn,
    CustomForeColor,
    CustomBackColor,
    Subscript,
    Superscript,
  ];
  return workflow ? tools : [...tools, ...[Print, Pdf]];
};

export const popupAlign = (horizontal, vertical) => {
  return {
    horizontal: horizontal,
    vertical: vertical,
  };
};

/**
 * It returns true if user is a member of project (owner/collaborators), else it returns false
 * @param {Object} project
 * @param {Int} id
 * @returns {Boolean}
 * @author Muskan Thakur
 */
export const accessBasedOnProjectMembers = (project, id) => {
  return splitString(project?.collaborators + "," + project?.owners, ",")
    ?.map(Number)
    ?.includes(id);
};

/**
 * returns url that is being shared from teams workflows
 * @param {int} flowId
 * @param {Int} projectId
 * @returns {String} url
 * @author Shivam Mishra
 */
export const getCreateTaskUrl = (flowId, projectId) => {
  let url = route.PRIVATE_ROUTE.TASKS.PATH;
  if (flowId && projectId) {
    url += `?createTask=${true}&fId=${flowId}&pId=${projectId}`;
  }
  return url;
};

/**removes pre-spacing and trailing space
 * @param {String} inputString
 * @author Muskan Thakur
 */
export const removeExtraSpaces = (inputString) => {
  if (!inputString || inputString.trim() === "") {
    return "";
  }
  const regexPattern = /\s+/g;
  return inputString.trim().replace(regexPattern, " ");
};

/**formats the given time in given format
 * @param {String} time
 * @param {String} format
 * @author Sarthak Arora
 * to utc format display
 */

export const formatTime = (time, timeFormat) => {
  if (!time) return format(new Date(`2000-01-01T${"09:00"}`), timeFormat);
  return format(new Date(`2000-01-01T${time}`), timeFormat);
};

/**
 * Removes special characters from the given email prefix.
 *
 * @param {string} emailPrefix - The email prefix from which to remove special characters.
 * *@example
 * email: brana+999@paymentus.com
 * emailPrefix: brana+999
 * @returns {string} The email prefix with special characters removed.
 * @author Bhavana
 */
export const removeSpecialCharacters = (emailPrefix) => {
  const SPECIAL_CHARACTER_REGEX = /[^\w]+/g;
  return emailPrefix.replace(SPECIAL_CHARACTER_REGEX, "");
};

/**
 * Asynchronous function to get the live timezone based on the user's geolocation.
 * @return {void}
 * @author Himanshi Chawla
 */
export const getLiveTimezone = async () => {
  return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  // return new Promise((resolve, reject) => {
  //   navigator.geolocation.getCurrentPosition(
  //     async (position) => {
  //       try {
  //         const { latitude, longitude } = position.coords;
  //         let liveTimezone = await store.dispatch(getTimezoneByCoordinates({ latitude, longitude }));
  //         resolve(liveTimezone?.timeZone);
  //       } catch (error) {
  //         resolve(label.DEFAULT_LOCATION);
  //       }
  //     },
  //     (error) => {
  //       let errorMessage;
  //       switch (error.code) {
  //         case error.PERMISSION_DENIED:
  //           errorMessage = quote.USER_DENIED_LOCATION;
  //           break;
  //         case error.POSITION_UNAVAILABLE:
  //           errorMessage = quote.INFORMATION_UNAVAILABLE;
  //           break;
  //         case error.TIMEOUT:
  //           errorMessage = quote.LOCATION_TIME_OUT;
  //           break;
  //         case error.UNKNOWN_ERROR:
  //         default:
  //           errorMessage = quote.UNKNOWN_ERROR;
  //           break;
  //       }
  //       store.dispatch(alertNotification(true, errorMessage, notifyIcon.WARNING_ICON));
  //       resolve(label.DEFAULT_LOCATION);
  //     }
  //   );
  // });
};

/**
 * Adds unique integers from an array to a comma-separated string,
 * avoiding duplicates.
 * @param {string} commaSeparatedString
 * @param {number[]} integersToAdd
 * @returns {string}
 * @author Shivam Mishra
 */
export function addIntegersToString(commaSeparatedString, integersToAdd) {
  let stringArray = commaSeparatedString ? splitString(commaSeparatedString, ",")?.map(Number) : [];

  integersToAdd?.forEach((integer) => {
    if (!stringArray?.includes(integer)) {
      stringArray.push(integer);
    }
  });
  return stringArray?.join(",");
}
/*
 * Checks if a given integer exists within a string of comma-separated integers.
 * @param {string} stringOfIntegers - A string containing comma-separated integers.
 * @param {number} targetInteger - The integer to search for within the string.
 * @returns {boolean} - True if the target integer exists in the string, false otherwise.
 * @author {Shivam}
 */
export function integerExistsInString(stringOfIntegers, targetInteger) {
  const arrayOfStrings = splitString(stringOfIntegers, ",");
  const arrayOfIntegers = arrayOfStrings?.map((str) => parseInt(str.trim(), number.TEN));
  return arrayOfIntegers?.includes(targetInteger);
}

/*
 * returns the sanitized html
 * @param {string} html - A string consisting of html
 * @returns {string} returns sanitized html string
 * @author {Sarthak Arora}
 */
export const sanitizeHtmlString = (html) => {
  return sanitizeHtml(html, {
    allowedTags: [
      "address",
      "article",
      "aside",
      "footer",
      "header",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "hgroup",
      "main",
      "nav",
      "section",
      "blockquote",
      "dd",
      "div",
      "dl",
      "dt",
      "figcaption",
      "figure",
      "hr",
      "li",
      "main",
      "ol",
      "p",
      "pre",
      "ul",
      "a",
      "abbr",
      "b",
      "bdi",
      "bdo",
      "br",
      "cite",
      "code",
      "data",
      "dfn",
      "em",
      "i",
      "kbd",
      "mark",
      "q",
      "rb",
      "rp",
      "rt",
      "rtc",
      "ruby",
      "s",
      "samp",
      "small",
      "span",
      "strong",
      "sub",
      "sup",
      "time",
      "u",
      "var",
      "wbr",
      "caption",
      "col",
      "colgroup",
      "table",
      "tbody",
      "td",
      "tfoot",
      "th",
      "thead",
      "tr",
      "img",
    ],
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ["href", "title", "target"],
      img: ["src", "alt", "title", "width", "height", "style", "align", "target"],
    },
    selfClosing: ["img", "br", "hr", "area", "base", "basefont", "input", "link", "meta"],
    parseStyleAttributes: true,
    allowedSchemes: ["data", "http", "https", "mailto"],
  });
};

/**
 * @returns projects with project privacy icon with their respective colors
 * @author Muskan Thakur
 */
export const itemRenderWorkflowAssignId = (li, itemProps, currentTheme) => {
  let colorCode = currentTheme == "dark" ? "white" : "#000000";
  const itemChildren = (
    <span key={itemProps?.index} className='custom-assignee-dropdown-item text-truncate d-flex flex-column' title={itemProps.dataItem.label}>
      <div className='float-left d-flex text-center'>
        <div
          className={`mr-2 ${
            itemProps.dataItem.Privacy == number.ONE ? "black-icon" : itemProps.dataItem.Privacy == number.TWO ? "sky-blue-icon" : itemProps.dataItem.Privacy == number.THREE ? "maroon" : itemProps.dataItem.Privacy == number.FOUR ? "black-icon" : "black-icon"
          }`}>
          {icon["PROJECT_ICON"]}
        </div>
        <span>{itemProps.dataItem.label}</span>
      </div>
    </span>
  );
  return React.cloneElement(li, li.props, itemChildren);
};

/**
 * @returns project with project privacy icon with its respective color
 */
export const valueRenderWorkflowAssignId = (element, value, currentTheme) => {
  let colorCode = currentTheme == "dark" ? "white" : "#000000";
  if (!value) {
    return element;
  }
  const children = [
    <div className='float-left d-flex text-center' key={value?.value}>
      <span className='mr-2' style={{ color: value.Privacy == number.ONE ? "#212529" : value.Privacy == number.TWO ? "#007BFF" : value.Privacy == number.THREE ? "#820000" : value.Privacy == number.FOUR ? "#212529" : "#212529" }}>
        {icon["PROJECT_ICON"]}
      </span>
      <span key={value?.value}>{value.label}</span>
    </div>,
  ];
  return React.cloneElement(element, { ...element.props }, children);
};

/**
 * Creates a Date object while preserving the date (ignoring time and time zone).
 * @param {string|Date} input - The input date string or Date object.
 * @returns {Date} - A Date object with the date preserved and time set to local midnight.
 * @author Muskan Thakur
 */
export const getDateAtMidnight = (input) => {
  const dateObj = typeof input === "string" ? new Date(input.split("T")[0]) : new Date(input);

  dateObj.setHours(0, 0, 0, 0);
  return dateObj;
};

/**truncates long name
 * @author Muskan Thakur
 */
export const truncateName = (name, maxLength = number.TWENTY_FIVE) => {
  return name.length > maxLength ? name.substring(number.ZERO, maxLength) + "..." : name;
};

/**
 * Generates a cryptographically secure unique ID that can never be duplicated
 * @param {string} userId - The ID of the current user (defaults to "anonymous" if not provided)
 * @returns {string} A unique identifier combining timestamp, user ID, and cryptographic random bytes
 * @author Abhishek Dhillon
 */
export const generateYdocRoomId = (userId = "anonymous") => {
  const timestamp = Date.now();
  const randomBytes = CryptoJS.lib.WordArray.random(8); // 8 bytes = 64 bits of randomness
  const randomHex = CryptoJS.enc.Hex.stringify(randomBytes);
  let uniqueId = `ydoc_${userId}_${timestamp}_${randomHex}`;
  return uniqueId;
};

/**
 * Sorts an array of objects alphabetically by a specified field
 * @param {Array} array - The array of objects to sort
 * @param {string} fieldName - The field name to sort by
 * @returns {Array} The sorted array
 * @author Sarthak Arora
 */
export const sortAlphabetically = (array, fieldName) => {
  if (!array || !Array.isArray(array) || !fieldName) {
    return array;
  }
  return [...array].sort((a, b) => {
    const valueA = a[fieldName]?.toString().toLowerCase() || "";
    const valueB = b[fieldName]?.toString().toLowerCase() || "";
    return valueA.localeCompare(valueB);
  });
};

export const convertToTimeZone = (time, format = "dd MMM yy") => {
  if(!time) return null;
  const state = store.getState();
  const userTimezone = state?.auth?.user?.timezone;
  // console.log("time", time , "format", format , "userTimezone", userTimezone);
  if (userTimezone) {
    return formatInTimeZone(time, userTimezone, format);
  }
  return formatInTimeZone(time, 'UTC', format);
};

/**
*This function converts a universal UTC timestamp into a "local" Date object that mimics a specific timezone's time.
 * @param {number|Date} instant - UTC milliseconds or Date
 * @param {string} [userTimezone] - IANA TZ; defaults to Redux auth.user.timezone
 */
export const reminderLocalMomentFromInstant = (instant, userTimezone) => {
  const tz = userTimezone ?? store.getState()?.auth?.user?.timezone ?? "UTC";
  const ms = typeof instant === "number" ? instant : instant.getTime();
  const str = formatInTimeZone(new Date(ms), tz, "yyyy-MM-dd HH:mm");
  const [datePart, timePart] = str.split(" ");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [H, M] = timePart.split(":").map(Number);
  return new Date(y, mo - 1, d, H, M, 0, 0);
};

/**
 * Next calendar day in user timezone at the given default time (e.g. UserReminderTime).
 * @param {Date} defaultTime - parseTimeStringToDate profile time
 * @param {string} [userTimezone] - IANA TZ; defaults to Redux auth.user.timezone
 */
export const reminderLocalMomentTomorrowAtDefaultTime = (defaultTime, userTimezone) => {
  const tz = userTimezone ?? store.getState()?.auth?.user?.timezone ?? "UTC";
  const todayStr = formatInTimeZone(new Date(), tz, "yyyy-MM-dd");
  const [y, mo, d] = todayStr.split("-").map(Number);
  const h = defaultTime.getHours();
  const mi = defaultTime.getMinutes();
  return new Date(y, mo - 1, d + 1, h, mi, 0, 0);
};

/**
 * Build combined UTC date from ReminderDate + ReminderTime (UTC inputs; time from timeString preferred).
 * @param {string} dateString - ReminderDate (e.g. "2025-02-13" or ISO with T)
 * @param {string} [timeString] - ReminderTime (e.g. "14:30")
 * @returns {Date|null} UTC date or null if invalid
 */
export const getCombinedReminderUtcDate = (dateString, timeString) => {
  try {
    if (!dateString) return null;
    const datePart = dateString.includes("T") ? dateString.split("T")[0] : dateString.includes("-") ? dateString : null;
    if (!datePart) return null;
    let timePart = "00:00";
    if (timeString) {
      const t = timeString.split(":").slice(0, 2).join(":");
      if (/^\d{1,2}:\d{2}$/.test(t)) timePart = t;
    }
    const utcIso = `${datePart}T${timePart}:00.000Z`;
    const date = new Date(utcIso);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

/**
 * Format reminder time in user timezone (combined ReminderDate + ReminderTime as UTC, then converted).
 * Use this for reminder time display instead of formatTime when date+time are UTC.
 * @param {string} dateString - ReminderDate
 * @param {string} timeString - ReminderTime
 * @param {string} [timeFormat] - Display format (default "hh:mm aa")
 * @returns {string|null} Formatted time or null
 */
export const formatReminderTimeInTimeZone = (dateString, timeString, timeFormat = "hh:mm aa") => {
  if (!timeString) return null;
  const combined = getCombinedReminderUtcDate(dateString, timeString);
  return combined ? convertToTimeZone(combined, timeFormat) : null;
};

/**
 * Formats a date for display in form
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 * @author Bhavana
 */
export const formatFormDate = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const state = store.getState();
  const { timezone } = state.auth.user;
  const formattedDate = formatInTimeZone(dateObj, timezone, dateFormat.FORM_DATE_FORMAT);


  return formattedDate;
};
